import { useState, useCallback } from 'react';
import { getIncidents, getIncident, createIncident, addIncidentComment } from '../utils/api';

export function useIncidents() {
  const [incident, setIncident] = useState(null);   // loaded incident detail
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Find open/in-progress incident for a given room+sensor
  const findActiveIncident = useCallback(async (roomCode, sensorKey) => {
    setLoading(true);
    setError(null);
    setIncident(null);
    try {
      const { incidents } = await getIncidents({
        room_code: roomCode,
        estado: 'abierto,en_progreso',
      });
      const match = incidents.find(i => i.sensor_key === sensorKey);
      if (match) {
        const { incident: full, comments } = await getIncident(match.id);
        setIncident({ ...full, comments });
      }
      return match || null;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new incident and load it
  const create = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      const { id } = await createIncident(payload);
      const { incident: full, comments } = await getIncident(id);
      setIncident({ ...full, comments });
      return id;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  // Add a comment (and optionally change status)
  const addComment = useCallback(async (incidentId, contenido, nuevo_estado = null) => {
    setSaving(true);
    setError(null);
    try {
      await addIncidentComment(incidentId, { contenido, nuevo_estado });
      const { incident: full, comments } = await getIncident(incidentId);
      setIncident({ ...full, comments });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, []);

  const reset = useCallback(() => {
    setIncident(null);
    setError(null);
  }, []);

  return { incident, loading, saving, error, findActiveIncident, create, addComment, reset };
}
